/**
 * API client for the survey player.
 * All endpoints are public (no auth required).
 */

import axios, { AxiosError, type AxiosInstance } from 'axios';

/**
 * The player prepends `/api/v1` to every request path (see lib/api/*.ts), so
 * `VITE_API_URL` must be the ORIGIN only — e.g. `https://research-api.example.com`.
 * The sibling web app uses the opposite convention (`NEXT_PUBLIC_API_URL` INCLUDES
 * `/api/v1`), so a copy-paste of that value lands here as
 * `…/api/v1` and yields a doubled `/api/v1/api/v1/…` 404. Normalize defensively:
 * strip a trailing slash and a trailing `/api/v1` so either value works.
 */
export function normalizeApiOrigin(raw: string): string {
  return raw.replace(/\/+$/, '').replace(/\/api\/v1$/, '');
}

const API_URL = normalizeApiOrigin(
  import.meta.env.VITE_API_URL || 'http://localhost:3001',
);

/**
 * Create axios instance for API calls.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

/**
 * API error interface.
 */
export interface ApiError {
  message: string;
  statusCode: number;
  error?: string;
}

/**
 * Check if error is an API error.
 */
export function isApiError(error: unknown): error is AxiosError<ApiError> {
  return axios.isAxiosError(error);
}

/**
 * Get error message from API error.
 */
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    const data = error.response?.data;
    if (data?.message) {
      return data.message;
    }
    if (error.message) {
      return error.message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

/**
 * Response interceptor for error handling.
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    // Log errors in development
    if (import.meta.env.DEV) {
      console.error('API Error:', {
        url: error.config?.url,
        status: error.response?.status,
        message: error.response?.data?.message,
      });
    }

    return Promise.reject(error);
  }
);
